import { useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../../modules/auth/core/Auth';
import styleCss from './styles.module.scss';

// Mensajero vigente de Dialogflow CX. El anterior (messenger-cx/bootstrap.js)
// no permitía enviar parámetros de consulta, así que usuario, ambiente y token
// viajaban dentro del identificador de sesión, que queda en el historial de
// conversaciones y en los registros del agente.
const DF_MESSENGER_JS = 'https://www.gstatic.com/dialogflow-console/fast/df-messenger/prod/v1/df-messenger.js';
const DF_MESSENGER_CSS = 'https://www.gstatic.com/dialogflow-console/fast/df-messenger/prod/v1/themes/df-messenger-default.css';

// Usuario al que pertenece la conversación guardada por el mensajero en esta pestaña
const USUARIO_SESION = 'chatico-usuario';
// Prefijo con que el mensajero guarda su estado en sessionStorage
const PREFIJO_MENSAJERO = 'df-messenger-';

const cargarMensajero = () => {
	if (!document.getElementById('chatico_vcc_css')) {
		const link = document.createElement('link');
		link.id = 'chatico_vcc_css';
		link.rel = 'stylesheet';
		link.href = DF_MESSENGER_CSS;
		document.head.appendChild(link);
	}
	if (!document.getElementById('chatico_vcc')) {
		const script = document.createElement('script');
		script.id = 'chatico_vcc';
		script.src = DF_MESSENGER_JS;
		script.async = true;
		document.body.appendChild(script);
	}
};

// Si la conversación guardada en la pestaña es de otra cuenta, se descarta
// antes de montar el mensajero, para que arranque con una sesión nueva.
// No se usa startNewSession(): borra también los parámetros de consulta y
// lanza la bienvenida sin usuario, que el agente trata como anónima.
const descartarConversacionAjena = (usuario: string) => {
	try {
		const anterior = sessionStorage.getItem(USUARIO_SESION);
		if (anterior && anterior !== usuario) {
			Object.keys(sessionStorage)
				.filter((clave) => clave.startsWith(PREFIJO_MENSAJERO))
				.forEach((clave) => sessionStorage.removeItem(clave));
		}
		sessionStorage.setItem(USUARIO_SESION, usuario);
	} catch (e) {
		// sin sessionStorage el mensajero no guarda conversación que reutilizar
	}
};

const Chatbot = () => {
	const { auth } = useAuth();
	const usuario = auth?.user?.id ? String(auth.user.id) : '';
	const bienvenidaMostrada = useRef('');

	// Durante el render, antes de que el mensajero se monte y lea su estado
	useMemo(() => {
		if (usuario) {
			descartarConversacionAjena(usuario);
		}
	}, [usuario]);

	useEffect(() => {
		cargarMensajero();
	}, []);

	useEffect(() => {
		if (!usuario) {
			return;
		}

		// El usuario y el token van como parámetros de consulta en cada
		// petición, no en el identificador de sesión, que queda opaco.
		const configurar = () => {
			const dfMessenger: any = document.querySelector('df-messenger');
			if (!dfMessenger?.setQueryParameters) {
				return;
			}
			dfMessenger.setQueryParameters({
				parameters: {
					id_usuario: usuario,
					token: auth?.api_token,
				},
			});
			if (bienvenidaMostrada.current === usuario) {
				return;
			}
			bienvenidaMostrada.current = usuario;
			dfMessenger.renderCustomCard([
				{
					"type": "info",
					"title": `¡Hola, ${auth?.user?.nick}! Soy Chatico, tu asistente virtual de empleo! `,
					"subtitle": `Escribe "Inicio" para volver al punto inicial de nuestra conversación.`,
				}
			]);
		};

		// Si el mensajero ya estaba cargado el evento no vuelve a llegar
		const alCargar = () => configurar();
		window.addEventListener('df-messenger-loaded', alCargar);
		customElements.whenDefined('df-messenger').then(() => setTimeout(configurar, 0));

		return () => window.removeEventListener('df-messenger-loaded', alCargar);
	}, [usuario, auth?.user?.nick, auth?.api_token]);

	if (!usuario) {
		return null;
	}

	// key: otra cuenta monta un mensajero nuevo, que lee el estado ya limpio
	return (
		<div key={usuario} className={`${styleCss['dft-messenger']}`}>
			<df-messenger
				location='global'
				project-id={`${process.env.REACT_APP_CHATICO_PROJECT_ID}`}
				agent-id={`${process.env.REACT_APP_CHATICO_AGENT_ID}`}
				language-code='es'
				intent='WELCOME'
				max-query-length='-1'
			>
				<df-messenger-chat-bubble chat-title='Chatico' />
			</df-messenger>
		</div>
	);
};

export default Chatbot;
